import React, { useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';
import { useParams } from 'react-router-dom';

import { EMetric } from 'api/useAnalytics';
import { StatsResult } from 'api/useAnalytics';
import { useAnalyticsAttributeNames } from 'api/useAnalyticsAttributeNames';
import { useAnalyticsAttributeValues } from 'api/useAnalyticsAttributeValues';
import { BaseStatistic } from 'views/Statistics/StatisticsView.types';

import { AttributeTrendsBox } from 'components/AnalyticsBox/AttributeTrendsBox/AttributeTrendsBox';
import { KPISummaryTable } from 'components/AnalyticsBox/KPISummaryTable/KPISummaryTable';
import { Flex } from 'components/ui/flex';

import {
  BUSINESS_ANALYTICS,
  DEFAULT_DATE_RANGE,
  getWholeRange
} from '../utils';

import ABTestEmptyState from './ABTestEmptyState';
import ABTestTabHeader from './ABTestTabHeader';
import { useStatsQueries } from './hooks/useStatsQueries';
import { useTrendsQueries } from './hooks/useTrendsQueries';

const AnalyticsABTestTab: React.FC = () => {
  const { publisherId } = useParams<{ publisherId: string }>();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [selectedAttributeName, setSelectedAttributeName] = useState<string | undefined>();
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<string[]>([]);

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);

  const { data: attributeNamesData, isLoading: isLoadingAttributeNames } =
    useAnalyticsAttributeNames({
      publisherId,
      startDate: apiDateRange!.from!.toISOString(),
      endDate: apiDateRange!.to!.toISOString(),
    });

  const { data: attributeValuesData, isLoading: isLoadingAttributeValues } =
    useAnalyticsAttributeValues({
      publisherId,
      attributeNames: selectedAttributeName ? [selectedAttributeName] : [],
      startDate: apiDateRange!.from!.toISOString(),
      endDate: apiDateRange!.to!.toISOString(),
    });

  const attributeNameOptions = useMemo(() => {
    return attributeNamesData?.attributeNames.map((name: string) => ({ label: name, value: name })) || [];
  }, [attributeNamesData]);

  const attributeValueOptions = useMemo(() => {
    if (!selectedAttributeName || !attributeValuesData?.values) {
      return [];
    }
    const values = attributeValuesData.values[selectedAttributeName] || [];
    const isDisabled = !selectedAttributeName || isLoadingAttributeValues;
    return values.map((value: string) => ({ label: value, value, disabled: isDisabled }));
  }, [attributeValuesData, selectedAttributeName, isLoadingAttributeValues]);

  const statsQueries = useStatsQueries({
    publisherId,
    selectedAttributeName,
    selectedAttributeValues,
    apiDateRange: {
      from: apiDateRange?.from?.toISOString(),
      to: apiDateRange?.to?.toISOString(),
    },
  });

  const trendsQueries = useTrendsQueries({
    publisherId,
    selectedAttributeName,
    selectedAttributeValues,
    apiDateRange: {
      from: apiDateRange?.from?.toISOString(),
      to: apiDateRange?.to?.toISOString(),
    },
  });

  const isLoading = statsQueries.some(q => q.isLoading) || trendsQueries.some(q => q.isLoading);

  const statsData = useMemo(() => {
    const data: Record<string, StatsResult[]> = {};
    statsQueries.forEach((query, index) => {
        if(query.data) {
            const value = selectedAttributeValues[index];
            data[value] = query.data;
        }
    });
    return data;
  }, [statsQueries, selectedAttributeValues]);

  const trendsData = useMemo(() => {
    const data: Record<EMetric, BaseStatistic[]> = {} as any;

    BUSINESS_ANALYTICS.forEach(metric => {
        data[metric] = [];
    });

    trendsQueries.forEach((query, index) => {
        if(query.data) {
            const value = selectedAttributeValues[index];
            query.data.forEach(trendResult => {
                const metric = trendResult.name;
                if(data[metric]) {
                    data[metric].push({
                        name: value,
                        values: trendResult.values,
                    });
                }
            });
        }
    });
    return data;
  }, [trendsQueries, selectedAttributeValues]);

  const handleAttributeNameChange = (value: string | undefined) => {
    setSelectedAttributeName(value);
    setSelectedAttributeValues([]);
  }

  return (
    <Flex className="gap-8">
      <ABTestTabHeader
        attributeNameOptions={attributeNameOptions}
        attributeValueOptions={attributeValueOptions}
        selectedAttributeName={selectedAttributeName}
        selectedAttributeValues={selectedAttributeValues}
        dateRange={dateRange}
        onAttributeNameChange={handleAttributeNameChange}
        onAttributeValuesChange={setSelectedAttributeValues}
        onDateRangeChange={setDateRange}
        isLoadingAttributeNames={isLoadingAttributeNames}
        isLoadingAttributeValues={isLoadingAttributeValues}
      />

      {selectedAttributeValues.length === 0 && (
        <ABTestEmptyState />
      )}

      {selectedAttributeValues.length > 0 && (
         <Flex className="gap-8">
            <KPISummaryTable
                attributeValues={selectedAttributeValues}
                statsData={statsData}
                isLoading={isLoading}
                metrics={BUSINESS_ANALYTICS}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {BUSINESS_ANALYTICS.map(metric => (
                    <AttributeTrendsBox
                        key={metric}
                        isLoading={isLoading}
                        metricName={metric}
                        trends={trendsData[metric] ?? []}
                    />
                ))}
            </div>
        </Flex>
      )}
    </Flex>
  );
};

export default AnalyticsABTestTab; 